import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ACTOR_PHRASES } from '../metadata/actor_sentence';
import AgentMetadata from '../metadata/agent_metadata';
import ApiMetadata from '../metadata/api_metadata';
import Communicator, {
  isApiInitiator as checkIsApiInitiator,
  isGladlyAiInitiator,
  isRuleInitiator,
} from 'models/communicator';
import connect from 'components/lib/connect';
import ConversationItem from 'models/conversation_item';
import ItemContent from '../item_content';
import Metadata from '../metadata/metadata';
import PrepositionalPhrase from '../metadata/prepositional_phrase';
import RuleLink from '../metadata/rule_link';

export function TopicChangeItem({ addedTopics, item, removedTopics }) {
  const { t } = useTranslation();
  const isApiInitiator = checkIsApiInitiator(item.initiator);
  const content = getTopicChangeContent();

  return (
    <ItemContent isAuditItem item={item}>
      {content}
    </ItemContent>
  );

  function getTopicChangeContent() {
    if (item.initiator.type === Communicator.AGENT) {
      return getAgentTopicChangeContent();
    }

    if (isApiInitiator) {
      return getNonAgentTopicChangeContent(ApiMetadata);
    }

    return getAutomatedTopicChangeContent();
  }

  // The topic list rides inside the sentence rather than arriving as a separate
  // `object`, so a translation can place it — and so the verb can agree with the
  // actor. The Spanish for these was second-person regardless of who acted,
  // which read as "Michelle Smith agregaste tema" for anyone but the reader.
  function getAgentTopicChangeContent() {
    return (
      <>
        {addedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="conversationTopicAdded">
            <AgentMetadata
              item={item}
              phrase={addedTopics.length > 1 ? ACTOR_PHRASES.ADDED_TOPICS : ACTOR_PHRASES.ADDED_TOPIC}
              phraseValues={{ topics: addedTopics.join(', ') }}
            />
          </TopicChangeDisplayItem>
        ) : null}
        {removedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="conversationTopicRemoved">
            <AgentMetadata
              item={item}
              phrase={removedTopics.length > 1 ? ACTOR_PHRASES.REMOVED_TOPICS : ACTOR_PHRASES.REMOVED_TOPIC}
              phraseValues={{ topics: removedTopics.join(', ') }}
            />
          </TopicChangeDisplayItem>
        ) : null}
      </>
    );
  }

  function getAutomatedTopicChangeContent() {
    return (
      <>
        {addedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="conversationTopicAdded">
            <Metadata
              isInbound={false}
              prepositionalPhrases={getNonAgentTopicPrepositionalPhrases(addedTopics, 'added')}
              timestamp={item.timestamp}
            />
          </TopicChangeDisplayItem>
        ) : null}
        {removedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="conversationTopicRemoved">
            <Metadata
              isInbound={false}
              prepositionalPhrases={getNonAgentTopicPrepositionalPhrases(removedTopics, 'removed')}
              timestamp={item.timestamp}
            />
          </TopicChangeDisplayItem>
        ) : null}
      </>
    );
  }

  function getNonAgentTopicChangeContent(MetadataComponent) {
    return (
      <>
        {addedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="conversationTopicAdded">
            <MetadataComponent
              item={item}
              prepositionalPhrases={getNonAgentTopicPrepositionalPhrases(addedTopics, 'added')}
            />
          </TopicChangeDisplayItem>
        ) : null}
        {removedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="conversationTopicRemoved">
            <MetadataComponent
              item={item}
              prepositionalPhrases={getNonAgentTopicPrepositionalPhrases(removedTopics, 'removed')}
            />
          </TopicChangeDisplayItem>
        ) : null}
      </>
    );
  }

  function getNonAgentTopicPrepositionalPhrases(topics, action) {
    const prepositionalPhrases = [];

    prepositionalPhrases.push(
      <PrepositionalPhrase
        complement={topics.join(', ')}
        key="topic"
        preposition={`Topic${topics.length > 1 ? 's' : ''}`}
      />
    );

    let complement = '';
    let preposition = `${action}`;
    if (isRuleInitiator(item.initiator)) {
      complement = <RuleLink ruleId={item.initiator.id}>{t('rule')}</RuleLink>;
      preposition = `${preposition} by`;
    } else if (isGladlyAiInitiator(item.initiator)) {
      complement = 'Gladly AI';
      preposition = `${preposition} by`;
    }

    prepositionalPhrases.push(<PrepositionalPhrase complement={complement} key="by" preposition={preposition} />);
    return prepositionalPhrases;
  }
}

TopicChangeItem.propTypes = {
  addedTopics: PropTypes.arrayOf(String).isRequired,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  removedTopics: PropTypes.arrayOf(String).isRequired,
};

const TopicChangeDisplayItem = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  max-width: 100%;
`;

function mapStateToProps({ getProvider }, { item }) {
  let addedTopics = _.sortBy(getTopicNames(item.content.addedTopics), topic => topic.toLowerCase());
  let removedTopics = _.sortBy(getTopicNames(item.content.removedTopics), topic => topic.toLowerCase());

  return {
    addedTopics,
    item,
    removedTopics,
  };

  function getTopicNames(topicIds) {
    let topicNames = [];
    topicIds.forEach(topicId => {
      let topic = getProvider('topics').findBy({ id: topicId });
      if (topic) {
        topicNames.push(topic.getNameWithAncestry());
      }
    });
    return topicNames;
  }
}

const TopicChangeItemContainer = connect(mapStateToProps, null)(TopicChangeItem);
TopicChangeItemContainer.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

export default TopicChangeItemContainer;
